import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';
import { useLocation } from '@reach/router';
import DetailViewLayout from '#/components/DetailViewLayout';
import DetailNavHeader from '#/shared/components/DetailNavHeader';
import ErrorWithCountdown from '#/shared/components/ErrorWithCountdown';
import SEO from '#/shared/components/SEO';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import LocationDetailHeader from '#/transit-tracker/components/LocationDetailHeader';
import StopsList from '#/transit-tracker/components/StopsList';
import TimestampRefreshButton from '#/shared/components/TimestampRefreshButton';
import useFavoriteStops from '#/transit-tracker/hooks/useFavoriteStops';
import useNearbyOrFavoriteArrivals from '#/transit-tracker/hooks/useNearbyOrFavoriteArrivals';
import useNavigate from '#/shared/hooks/useNavigate';
import ROUTES from '#/constants/routes';
import useSelectedLocation from '#/shared/hooks/useSelectedLocation';
import useCurrentQueryRedirect from '#/shared/hooks/useCurrentQueryRedirect';
import useViewMapControls from '#/map/hooks/useViewMapControls';
import useHighlightedStop from '#/transit-tracker/hooks/useHighlightedStop';
import {
  ORIGIN_LOCATION_TYPE,
  DESTINATION_LOCATION_TYPE,
  ARRIVALS_REFRESH_INTERVAL,
} from '#/shared/constants';
import GTM_HANDLES from '#/shared/gtm-handles';
import useInterval from '#/shared/hooks/useInterval';
import { setSelectedLocationSuccess } from '#/shared/slices/location';
import * as Styled from './styled';

function LocationDetailContainer() {
  const { search: searchParams } = useLocation();
  const { matchPath } = useNavigate();
  const isLocationRoute = matchPath([ROUTES.location]);
  const dispatch = useDispatch();
  const {
    navigate,
    navigateToStopAndSign,
    generateNavigateBack,
    generateNavigateClose,
  } = useNavigate();
  const { selectedLocation } = useSelectedLocation();
  const isArrivalsError = useSelector(s => s.transitTracker.isArrivalsError);
  const { setCurrentQueryParam } = useCurrentQuery();
  const { toggleFavoriteStop, isFavoriteRouteStop } = useFavoriteStops();
  const {
    nearbyLocationArrivals: arrivals,
    nearbyStops,
    arrivalsTimestamp,
    refreshArrivals,
    clearLocationArrivals,
  } = useNearbyOrFavoriteArrivals(false);

  const { locationDetailViewOpened, viewClosed } = useViewMapControls();

  useEffect(() => {
    locationDetailViewOpened();
    return viewClosed;
  }, [locationDetailViewOpened, viewClosed]);
  const { currentQueryRedirect } = useCurrentQueryRedirect();

  useInterval(refreshArrivals, ARRIVALS_REFRESH_INTERVAL);

  useEffect(() => {
    return () => {
      clearLocationArrivals();
    };
  }, [clearLocationArrivals]);

  useEffect(() => {
    if (selectedLocation) {
      clearLocationArrivals();
      refreshArrivals();
    }
  }, [selectedLocation, refreshArrivals, clearLocationArrivals]);

  useEffect(() => {
    if (!selectedLocation) {
      if (isLocationRoute) {
        dispatch(setSelectedLocationSuccess(queryString.parse(searchParams)));
      } else {
        navigate(ROUTES.search);
      }
    }
  }, [dispatch, selectedLocation, navigate, searchParams, isLocationRoute]);

  const navigateToPlanner = useCallback(
    () => navigate(ROUTES.planner),
    [navigate]
  );

  const navigateToTrip = useCallback(() => {
    navigate(ROUTES.trip);
  }, [navigate]);

  const onLocationSelected = useCallback(
    locType => {
      setCurrentQueryParam(locType, selectedLocation);
      currentQueryRedirect(locType, navigateToPlanner, navigateToTrip);
    },
    [
      currentQueryRedirect,
      setCurrentQueryParam,
      navigateToTrip,
      navigateToPlanner,
      selectedLocation,
    ]
  );
  const { setHighlightedStop } = useHighlightedStop();

  const showRefreshButton = nearbyStops?.length > 0;
  const locationName = selectedLocation?.name || selectedLocation?.address;

  return (
    <>
      {selectedLocation && (
        <SEO metadataId="location" dynamicValues={{ locationName }} />
      )}
      <DetailViewLayout
        top={
          <DetailNavHeader
            onBack={generateNavigateBack()}
            onClose={generateNavigateClose()}
            content={<FormattedMessage id="detailview.locationtitle" />}
          />
        }
        middle={
          selectedLocation && (
            <LocationDetailHeader
              location={selectedLocation}
              navigateToTripPlannerTo={() =>
                onLocationSelected(DESTINATION_LOCATION_TYPE)
              }
              navigateToTripPlannerFrom={() =>
                onLocationSelected(ORIGIN_LOCATION_TYPE)
              }
            />
          )
        }
        bottom={
          isArrivalsError ? (
            <ErrorWithCountdown
              refreshInterval={ARRIVALS_REFRESH_INTERVAL}
              errorMessageId="arrivals.error"
            />
          ) : (
            <>
              {showRefreshButton && (
                <Styled.ArrivalsHeader>
                  <TimestampRefreshButton
                    timestamp={arrivalsTimestamp}
                    onClick={refreshArrivals}
                  />
                </Styled.ArrivalsHeader>
              )}
              <StopsList
                arrivals={arrivals || []}
                toggleFavoriteStop={toggleFavoriteStop}
                isFavoriteStop={isFavoriteRouteStop}
                navigateToStopAndSign={navigateToStopAndSign}
                navigateToPlanner={navigateToPlanner}
                highlightStop={setHighlightedStop}
                dataGtm={GTM_HANDLES.location.locationDetailNearbyStops}
                componentMode="nearbyLocation"
                selectedLocation={selectedLocation}
              />
            </>
          )
        }
      />
    </>
  );
}

export default LocationDetailContainer;
